import { Box } from "@mui/material";
import { IconChevronLeft } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../../components/ui/Button";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import Text from "../../../components/ui/Text";
import { useToast } from "../../../contexts/ToastContext";
import apiService from "../../../services/api";
import { GamesList } from "./parts";

const GameHistory = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const observerRef = useRef(null);

  const [games, setGames] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    limit: 20,
    has_next_page: false,
  });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const handleBack = () => {
    navigate(-1);
  };

  const getGames = async (params, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const response = await apiService.fetchUserGames({
        to: new Date().getTime(),
        ...params,
      });

      if (response.success) {
        if (append) {
          setGames((prevGames) => [...prevGames, ...response.data]);
        } else {
          setGames(response.data);
        }
        setPagination(response.pagination);
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreGames = () => {
    if (!loadingMore && pagination.has_next_page) {
      const nextPage = pagination.current_page + 1;
      getGames({ page: nextPage, limit: pagination.limit }, true);
    }
  };

  useEffect(() => {
    getGames({ page: 1, limit: 20 });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && pagination.has_next_page && !loadingMore) {
          loadMoreGames();
        }
      },
      {
        root: null,
        rootMargin: "100px",
        threshold: 0.1,
      },
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [pagination.has_next_page, loadingMore, loadMoreGames]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {/* Back Button */}
      <Box sx={{ mb: 3, display: { xs: "block", md: "none" } }}>
        <Button variant="back" onClick={handleBack} />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <Box
          onClick={handleBack}
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            cursor: "pointer",
            color: "#1890FF",
            "&:hover": {
              color: "#40A9FF",
            },
          }}
        >
          <IconChevronLeft size={20} />
        </Box>
        <Text variant="pageHeader">Game history</Text>
      </Box>

      {/* Content Section with White Background on Desktop */}
      <Box
        sx={{
          backgroundColor: "transparent",
          borderRadius: 0,
          p: 0,
          mt: 3,
          mb: 4,
          border: "none",
          maxWidth: "600px",
        }}
      >
        {/* Game History List */}
        <GamesList
          games={games}
          pagination={pagination}
          observerRef={observerRef}
          loadingMore={loadingMore}
          emptyMessage="No past games found."
        />

        {/* Empty state if no games */}
        {games.length === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 2,
              px: 2,
            }}
          >
            <Button variant="primary" onClick={() => navigate("/upcoming")}>
              Browse upcoming games
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
};

export default GameHistory;
